// Copyright (c) Docugami, Inc. All rights reserved.

import { FontIcon, Icon, ITooltipHostProps, Link, Stack, TooltipHost, TooltipHostBase } from "@fluentui/react";
import { DgFlexSpacer, DisabledCss } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { SlidingFilterPanel } from "components/common/SlidingFilterPanel";
import { IconContainer } from "components/myContentExplorer/NavigationItemGroup";
import { useReportDocumentContext } from "components/reports/ReportDocumentContext";
import { disabledGreyText, emptyGray, greyText, homeGray, primaryTheme, themeBlue } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { IconDescriptor, IconRenderer } from "components/work_path/IconRenderer";
import { FilterCheckbox, useCheckboxFilters, useReportFiltersState } from "components/work_path/ReportFilterComponents";
import { useAddReportEntry } from "hooks/AddReportEntry";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { useLabelChunk } from "hooks/LabelChunk";
import { useModalSelectionActions, useModalSelectionAreaDefinition, useModalSelectionListener } from "hooks/ModalSelection";
import { ITagText, useTagText } from "hooks/TagText";
import { ISuggestedChunk, useWorkPathSuggestedChunks } from "hooks/WorkPathSuggestedChunks";
import { dgIconNames } from "hooks/dgIcons";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useReportEntryActions } from "hooks/reportEntryActions";
import { useReportEntryIdForChunk } from "hooks/reportEntryIdForChunk";
import { useConfirmDocumentBindings, useEntryBindingsForDocumentConfirmed } from "hooks/reports";
import { ChunkInstance } from "networking/api";
import { ReportEntry } from "networking/reportsApi";
import { useEffect, useMemo, useRef, useState } from "react";
import { ModalSelectionArea, ModalSelectionItemType } from "store/documents/types";
import { FeatureFlags } from "store/featureFlags/types";
import { ReportFilterAxis, setFilterAxis, setFilters, setSearchString } from "store/reports/reportsSlice";
import { createSelectFilteredReportEntriesForDocument, selectIsReportPublished, selectReportContext, selectReportLabels, selectSelectedEntry } from "store/reports/selectors";
import { selectCurrentChunkInstance, selectEditTokenRange, selectSelectedChunkInstance } from "store/sdvSelectors";
import styled from "styled-components";
import { AutomationId } from "utils/Automation";
import { ChunkResizing } from "utils/ChunkResizing";
import { toPascalCase } from "utils/StringUtils";
import { CommandButton, ICommand, ICommandButton, IconAlignment } from "./CommandButton";
import { ReportItemsList } from "./ReportItemsList";
import { SelectedItemCardlet } from "./SelectedItemCardlet";
import { SlidingPanel } from "./SlidingPanel";
import { ISuggestedItemListItem, SuggestedItemsList } from "./SuggestedItemsList";

const PaneRoot = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    font-family: ${dgFontFamily};
    >.reportsPanel {
        flex: 1;
        box-sizing: border-box;
        min-width: 300px;
    }
    .selectedItem {
        gap: 5px;
        padding-bottom: 5px;
    }
`

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 5px;
    box-sizing: border-box;
    color: ${homeGray};
`

const ConfirmAll = styled.div<{ isDisabled: boolean }>`
    margin-right: 0;
    margin-left: auto;
    cursor: pointer;
    text-transform: uppercase;
    font-size: 10px;
    line-height: 12px;
    opacity: ${props => props.isDisabled ? '.3' : '1'};
    pointer-events: ${props => props.isDisabled ? 'none' : 'inherited'};

`

const ConfirmAllContent = styled.div`
    display: flex;
    align-items: center;
`

const ConfirmIcon = styled(IconRenderer)`
    margin-left: 5px;
`

const PanelTitleText = styled.span`
    font-size: 14px;
    line-height: 16px;
    font-weight: 500;
    margin-right: 8px;
`

interface ReportsWorkPaneProps {
    docSetId: string;
    documentId: string;
    reportId: string;
    readOnly?: boolean
}

export const ReportsWorkPane = (props: ReportsWorkPaneProps): JSX.Element => {
    const [addEntryError, setAddEntryError] = useState<string>();

    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const editTokenRange = useAppSelector(selectEditTokenRange);
    const selectedEntryId = useAppSelector(selectSelectedEntry)?.id;

    const selectedChunkEntryId = useReportEntryIdForChunk(selectedChunkInstance);

    const instance = useAppSelector(selectCurrentChunkInstance);
    const tagText = useTagText(props.docSetId, props.documentId, instance);

    const { isLinking, isLinkingChunk, startLinkingChunk, canLinkChunk, errorModal: linkingErrorModal } = useReportDocumentContext().chunkLinkingState;

    const { labelChunk } = useLabelChunk(props.docSetId, props.documentId);

    const isReportPublished = useAppSelector(selectIsReportPublished);
    const reportLabels = useAppSelector(selectReportLabels);

    const onAddEntry = useAddReportEntry(props.documentId);

    const reportWorkPaneRef = useRef<HTMLDivElement | null>(null);
    const selectedItemRef = useRef<HTMLDivElement | null>(null);
    useModalSelectionAreaDefinition(ModalSelectionArea.ReportWorkPane, reportWorkPaneRef);
    useModalSelectionAreaDefinition(ModalSelectionArea.SelectedItem, selectedItemRef);

    const { confirm: confirmModalAction } = useModalSelectionActions();
    const { isActive: isChunkModalActive } = useModalSelectionListener([ModalSelectionItemType.Chunk]);

    async function addEntry(chunkInstance: ChunkInstance, label?: string, targetIndex?: number, groupId?: string) {
        try {
            await onAddEntry(chunkInstance, label, targetIndex, groupId);
        }
        catch {
            // Show a generic error message to the user when adding a new entry fails
            setAddEntryError("Something went wrong trying to add this item.");
        }
    }

    async function addSelectedChunkEntry() {
        if (!instance) {
            return;
        }

        if (isChunkModalActive && !isLinking) {
            // Check to see if the selected chunk was resized and update that operation before adding the entry
            let targetChunk = instance;
            if (!!selectedChunkInstance && !!editTokenRange) {
                // Resize
                targetChunk = await ChunkResizing.commitResize(selectedChunkInstance, editTokenRange);
            }

            if (!targetChunk) {
                return;
            }

            const label = tagText.tagText.trim();
            targetChunk = await labelChunk(targetChunk, label);
            confirmModalAction();
            await addEntry(targetChunk, label);
        }
        else if (!!selectedChunkInstance) {
            await addEntry(selectedChunkInstance, tagText.tagText.trim())
        }
    }

    function getAddEntryTooltip() {
        if (isReportPublished) {
            return (
                <div>
                    This project is published. To add items, edit the {reportLabels.titleName.toLocaleLowerCase()} using <br />
                    the <FontIcon iconName={'MoreVertical'} style={{ marginTop: 3 }} /> menu.
                </div>
            );
        } else if (!tagText.tagText) {
            return `This chunk does not have a label. Please label your item to be able to add it to your ${reportLabels.titleName.toLocaleLowerCase()}.`
        } else if (!tagText.isValid) {
            return `This chunk's label is invalid. Please input a valid label to be able to add your item to your ${reportLabels.titleName.toLocaleLowerCase()}.`
        }
    }

    const selectedItemFooter = (
        <ReportsSelectedItemFooter
            link={{
                canExecute: () => !isChunkModalActive && !!selectedChunkInstance && canLinkChunk(selectedChunkInstance),
                execute: async () => startLinkingChunk(selectedChunkInstance!, true)
            }}
            addEntry={{
                canExecute: () => !selectedChunkEntryId &&
                    !!instance &&
                    tagText.isValid &&
                    !!tagText.tagText &&
                    !isReportPublished,
                execute: addSelectedChunkEntry,
                tooltipContent: getAddEntryTooltip()
            }} />
    )

    const isOpen = !isLinkingChunk && !!instance && !selectedChunkEntryId && !selectedEntryId;
    return (
        <PaneRoot ref={reportWorkPaneRef}>
            <div ref={selectedItemRef}>
                <SelectedItemPanel
                    title="SELECTED ITEM"
                    isOpen={isOpen}
                    instance={instance}
                    docSetId={props.docSetId}
                    documentId={props.documentId}
                    footer={selectedItemFooter}
                    dndDisabled={!isOpen || isLinking}
                    tagText={tagText} />
            </div>
            <ReportItemsPanel title={`${reportLabels.itemsName.toLocaleUpperCase()} ITEMS`}
                docSetId={props.docSetId}
                documentId={props.documentId}
                reportId={props.reportId} />
            {linkingErrorModal}
            {props.readOnly ? <></> : (
                <SuggestedItemsPanel
                    disabled={!!selectedEntryId}
                    onAddEntry={addEntry}
                    reportId={props.reportId}
                    title="SUGGESTED ITEMS"
                    docSetId={props.docSetId}
                    documentId={props.documentId}
                    dndDisabled={isLinking} />)}
            <ConfirmationDialog
                hidden={!addEntryError}
                title={"Failed to add item"}
                subText={addEntryError}
                confirmButtonText="OK"
                confirmedCallback={() => setAddEntryError(undefined)}
                dismissedCallback={() => setAddEntryError(undefined)} />
        </PaneRoot>
    )
}

interface ConfirmAllButtonProps {
    allBindingsConfirmed?: boolean;
    confirming: boolean;
    confirm: () => void;
    className?: string;
}

const ConfirmAllButton = ({ allBindingsConfirmed, className, confirm, confirming }: ConfirmAllButtonProps) => {
    let tooltip = '';

    if (allBindingsConfirmed === true) {
        tooltip = 'All chunks for this document are confirmed';
    }

    if (confirming) {
        tooltip = 'Confirming chunks for this document';
    }

    return (
        <div className={className}>
            <TooltipHost content={tooltip}>
                <ConfirmAll isDisabled={allBindingsConfirmed || confirming} onClick={confirm}>
                    <ConfirmAllContent>
                        <span>confirm all</span>
                        <ConfirmIcon
                            source={{
                                name: dgIconNames.DgConfirmIcon,
                                width: 12,
                                height: 12,
                                defaultColor: allBindingsConfirmed ? primaryTheme.palette.white : homeGray,
                                backgroundColor: allBindingsConfirmed ? primaryTheme.palette.themePrimary : undefined
                            } as IconDescriptor} width={12} height={12} />
                    </ConfirmAllContent>
                </ConfirmAll>
            </TooltipHost>
        </div>
    )
}

const StyledConfirmAllButton = styled(ConfirmAllButton)`
    margin-left: auto;
`

const AdditionalFilterContent = styled(Stack)`
    flex-wrap: wrap;
    margin: 5px;
`

const AdditionalFilterItem = styled(Stack.Item)`
    margin-top: 5px;
`

interface ReportItemsPanelProps {
    title: string;
    docSetId: string;
    documentId: string;
    reportId: string;
}

// Creates a selector for filtered entries in a single document context
export const selectFilteredReportEntries = createSelectFilteredReportEntriesForDocument();
const ReportItemsPanel = (props: ReportItemsPanelProps): JSX.Element => {
    const context = useAppSelector(selectReportContext);

    const searchString = useAppSelector(state => state.reportsSlice.searchString);
    const filterAxis = useAppSelector(state => state.reportsSlice.filterAxis);

    const dispatch = useAppDispatch();

    const { isActive, countText } = useReportFiltersState();
    const checkboxFilters = useCheckboxFilters();
    const [showFilterPanel, setShowFilterPanel] = useState(filterAxis === ReportFilterAxis.Items && isActive);

    const reportListRef = useRef<HTMLDivElement | null>(null);
    useModalSelectionAreaDefinition(ModalSelectionArea.ReportItems, reportListRef);

    const reportEntries: ReportEntry[] = useAppSelector(state => selectFilteredReportEntries(state, props.reportId, context));
    const filteredEntryIds = useMemo(() => reportEntries.map(x => x.id), [reportEntries]);

    const allBindingsConfirmed = useEntryBindingsForDocumentConfirmed(props.documentId, filteredEntryIds);
    const { confirm, confirming, isDisabled: isConfirmAllHidden } = useConfirmDocumentBindings(props.documentId, filteredEntryIds);

    useEffect(() => {
        // Ensure that we are filtering against report items
        if (showFilterPanel) {
            dispatch(setFilterAxis(ReportFilterAxis.Items));
        }
    }, [showFilterPanel, dispatch])

    function onFiltersClick() {
        if (!showFilterPanel) {
            dispatch(setFilters([]));
        }

        setShowFilterPanel(v => !v);
    }

    return (
        <PanelRoot className="panel reportsPanel" ref={reportListRef}>
            <PanelHeader>
                <PanelTitleText className="title">{props.title}</PanelTitleText>
                <TooltipHostBase
                    content='FILTER ITEMS'>
                    <DgIconButton
                        iconName={dgIconNames.DgFilter}
                        onClick={onFiltersClick}
                        height={16}
                        width={16}
                        color={showFilterPanel ? themeBlue : homeGray} />
                </TooltipHostBase>
                {allBindingsConfirmed !== undefined && !isConfirmAllHidden &&
                    <StyledConfirmAllButton
                        allBindingsConfirmed={allBindingsConfirmed}
                        confirm={confirm}
                        confirming={confirming} />
                }
            </PanelHeader>
            <SlidingFilterPanel
                showFilterPanel={showFilterPanel}
                filterText={searchString}
                onFilterTextChange={(val) => dispatch(setSearchString(val ?? ''))}
                onClose={() => setShowFilterPanel(false)}
                countText={countText}
            >
                <AdditionalFilterContent horizontal>
                    {
                        checkboxFilters.map(filter => (
                            <AdditionalFilterItem
                                key={filter.key}
                                basis="50%"
                                align="center">
                                <FilterCheckbox filter={filter} />
                            </AdditionalFilterItem>
                        ))
                    }
                </AdditionalFilterContent>
            </SlidingFilterPanel>
            <ListContainer>
                <ReportItemsList
                    isDisabled={confirming}
                    documentId={props.documentId}
                    clusterId={props.docSetId}
                    reportId={props.reportId}
                    reportItems={reportEntries}
                    canDrop={!searchString}
                />
            </ListContainer>
        </PanelRoot >
    )
}

const PanelRoot = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    >.title {
        margin-left: 7px;
    }
`

const ListContainer = styled.div`
    flex: 1;
    display: flex;
    overflow: hidden;
`

const SelectedItemDivider = styled.div`
    padding-top: 5px;
    border-bottom: 1px solid ${emptyGray};
`;

interface SelectedItemPanelProps {
    title: string;
    isOpen: boolean;
    instance?: ChunkInstance;
    docSetId: string;
    documentId: string;
    footer?: JSX.Element;
    dndDisabled?: boolean;
    tagText: ITagText;
    editDisabled?: boolean;
}

const SelectedItemPanel = (props: SelectedItemPanelProps): JSX.Element => {
    return (
        <SlidingPanel isOpen={props.isOpen} durationS={.35}>
            <PanelRoot data-automation-id={props.isOpen ? AutomationId.WorkPathDocumentSelectedItemPanel : undefined} className="panel selectedItem">
                <PanelTitleText className="title">{props.title}</PanelTitleText>
                <SelectedItemCardlet clusterId={props.docSetId}
                    documentId={props.documentId}
                    instance={props.instance}
                    tagText={props.tagText}
                    footer={props.footer}
                    dndDisabled={props.dndDisabled} />
                <SelectedItemDivider />
            </PanelRoot>
        </SlidingPanel>
    )
}

interface SuggestedItemPanelProps {
    title: string,
    docSetId: string,
    reportId: string,
    documentId: string,
    dndDisabled?: boolean,
    onAddEntry: (chunkInstance: ChunkInstance, label?: string) => Promise<void>,
    disabled?: boolean
}

const SuggestedItemsRoot = styled.div<{ disabled?: boolean }>`
    border-top: 1px solid ${emptyGray};
    padding: 5px;
    max-height: 30%;
    display: flex;
    flex-direction: column;
    ${props => !!props.disabled && DisabledCss};
`

const SuggestedHeaderContainer = styled.div<{ isDisabled?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-right: 5px;
    padding-bottom: 10px;
    gap: 10px;
    height: 20px;
    :hover {
        cursor: ${props => !props.isDisabled ? 'pointer' : 'default'};
    }
`

const SuggestedItemsPanel = (props: SuggestedItemPanelProps): JSX.Element => {
    const { startLinkingChunk, canLinkChunk, } = useReportDocumentContext().chunkLinkingState;
    const { suggestedChunks, panelOpen, flipPanel } = useWorkPathSuggestedChunks();
    const isAddAllEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);
    const { execute: executeAddAllChunks, state: addAllChunksRequestState } = useAsyncHelper("Add All Suggestions", props.reportId, undefined);

    const { canAddEntry } = useReportEntryActions();

    const suggestedChunkListItems = useMemo(() => {
        function createSuggestedChunkListItem(suggestion: ISuggestedChunk): ISuggestedItemListItem {
            const footer = (
                <ReportsSelectedItemFooter
                    link={{
                        canExecute: () => canLinkChunk(suggestion.chunkInstance),
                        execute: async () => startLinkingChunk(suggestion.chunkInstance, true)
                    }}
                    addEntry={{
                        canExecute: () => canAddEntry,
                        execute: () => props.onAddEntry(suggestion.chunkInstance)
                    }} />
            );

            return {
                suggestion: suggestion,
                footer: footer
            }
        }

        return suggestedChunks.map(createSuggestedChunkListItem);
    }, [suggestedChunks, canLinkChunk, startLinkingChunk, canAddEntry, props])

    async function addAllSuggested(): Promise<void> {
        for (const suggestion of suggestedChunks) {
            await props.onAddEntry(suggestion.chunkInstance);
        }
    }

    return (
        <SuggestedItemsRoot disabled={props.disabled}>
            <SuggestedHeaderContainer onClick={flipPanel} isDisabled={panelOpen === undefined}>
                <PanelTitleText className="title">{props.title}</PanelTitleText>
                <DgFlexSpacer />
                {isAddAllEnabled && addAllChunksRequestState !== RequestState.InProgress && suggestedChunkListItems?.length > 0 ? <Link onClick={() => executeAddAllChunks(addAllSuggested())}>Add All</Link> : null}
                <IconContainer isExpanded={!!panelOpen}>
                    <Icon iconName={"ChevronRight"} color={panelOpen === undefined ? disabledGreyText : greyText} />
                </IconContainer>
            </SuggestedHeaderContainer>
            <SlidingPanel isOpen={!!panelOpen} durationS={.35} scrollable>
                <SuggestedItemsList
                    docSetId={props.docSetId}
                    documentId={props.documentId}
                    dndDisabled={!!props.dndDisabled && !panelOpen}
                    items={suggestedChunkListItems} />
            </SlidingPanel>
        </SuggestedItemsRoot>
    )
}

const FooterRoot = styled.div`
    flex: 1;
    margin: 0px 5px;
    display: flex;
    justify-content: space-between;
    flex-direction: row;
`

interface ReportsSelectedItemFooterProps {
    link?: ICommand;
    addEntry?: ICommand;
}

const ReportsSelectedItemFooter = (props: ReportsSelectedItemFooterProps): JSX.Element => {
    const reportLabels = useAppSelector(selectReportLabels);

    return (
        <FooterRoot>
            {!!props.link &&
                <ReportFooterButton
                    label={`LINK TO ITEM`}
                    icon={dgIconNames.DgChunkToolbarLinkIcon}
                    command={props.link}
                />
            }
            {!!props.addEntry &&
                <ReportFooterButton
                    label={`ADD TO ${reportLabels.itemsName.toLocaleUpperCase()}`}
                    icon={dgIconNames.DgPlus}
                    command={props.addEntry}
                    tooltipProps={{
                        content: props.addEntry?.tooltipContent,
                    }}
                />
            }
        </FooterRoot>
    );
}

interface ReportButtonProps {
    command: ICommand;
    icon: dgIconNames;
    label: string;
    tooltipProps?: ITooltipHostProps;
}

const ReportFooterButton = (props: ReportButtonProps): JSX.Element => {
    const commandButton: ICommandButton = {
        icon: {
            name: props.icon,
            width: 1,
            height: 1,
            defaultColor: greyText
        },
        label: props.label,
        showLabel: true,
        canExecute: props.command.canExecute(),
        onClick: () => props.command.execute(),
        tooltipProps: props.tooltipProps
    };

    return (
        <CommandButton
            iconAlignment={IconAlignment.Left}
            dataAutomationId={toPascalCase(commandButton.label, '')}
            command={commandButton}
            width={13}
            height={13} />
    )
}

